/**
 * CapsulePropTypes
 *
 * Provides custom utility validators for
 * Capsule-specific object shapes and types.
 */
import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import lowerFirst from 'lodash/lowerFirst';
import upperFirst from 'lodash/upperFirst';

import { SCHEMAS } from './schemas';

const DECORATED_OPTIONS = ['expiry', 'barcodePrefix'];

export const CapsulePropTypes = {} as ObjectWithUnknownKeys;

forEach(SCHEMAS, (options, schemaName) => {
  const capsuleType = typeFactory(schemaName, options);

  CapsulePropTypes[schemaName] = capsuleType;
  CapsulePropTypes[lowerFirst(schemaName)] = capsuleType; // for convenience
});

/**
 * Generates a chainable PropTypes validator given a simple validator function,
 * decorates `isRequired` and `schemaName` onto it.
 *
 * @returns A chainable PropTypes validator that may offer isRequired.
 */
function createChainableTypeChecker(
  /** Name of model in camelCase */
  schemaName: string
) {
  const validate = dataModelValidator(schemaName);

  function checkType(isRequired, props, propName, componentName, location, propFullName) {
    componentName = componentName || 'ANONYMOUS_COMPONENT';
    if (props[propName] == null) {
      // deliberate use of non-strict ==
      if (isRequired) {
        return new Error(`Required prop \`${propName}\` not found in ${componentName}.`);
      }
      return null;
    } else {
      return validate(props, propName, componentName, location, propFullName);
    }
  }

  const chainedCheckType = checkType.bind(null, false);
  chainedCheckType.isRequired = checkType.bind(null, true);

  return chainedCheckType;
}

/**
 * Generates a simple, custom PropTypes validator for models of type `type` (case-insensitive)
 *
 * @param  {String} type   data model name from JSON-API (e.g. 'OrderItem')
 * @return {Function}      a function acceptable for PropTypes' custom validator for dataModel of `type`
 */
function dataModelValidator(type) {
  return (props, propName, componentName, _location, propFullName) => {
    const p = props?.[propName];
    if (isNil(p)) return null;
    if (typeof p === 'object' && RegExp(`${p?.type ?? 'unknown'}$`, 'i').test(type)) {
      return null;
    }
    // handle PropTypes.arrayOf() custom validator signature
    if (isNumber(propName)) {
      propName = propFullName;
    }
    return new Error(
      `${componentName} prop \`${propName}\` expects a Capsule model of type \`${upperFirst(
        type
      )}\`, but one of type \`${p?.type ?? p}\` was provided.`
    );
  };
}

// export default CapsulePropTypes;

/**
 * Creates a CapsuleType validator with mocking features.
 *
 * @param  {String} schemaName
 * @param  {Object} options    Optional hash to help inform design of mocker and
 *                             decorate with helper data for portions of CapOS
 *                             that need to inform behavior when dealing with this data model
 *
 * @return {Function}          Custom PropTypes validator with decorated options and mock API
 */
export function typeFactory(schemaName, options) {
  const validator = createChainableTypeChecker(schemaName);
  (validator as any).schemaName = schemaName;

  // decorate whitelisted extra options
  forEach(options, (value, key) => {
    if (includes(DECORATED_OPTIONS, key)) {
      validator[key] = value;
    }
  });

  return validator;
}
